import React from "react";
import Pendulum3D from "./Pendulum3D";
import Parallelogram3D from "./Parallelogram3D";
import Circuit3D from "./Circuit3D";
import DNAHelix3D from "./DNAHelix3D";
import Mitosis3D from "./Mitosis3D";
import { Narrator } from "./Narrator";

// Experiments that have a 3D flagship scene (used for the "View in 3D" toggle).
export const HAS_3D = new Set([
  "phy-9-pendulum",
  "phy-11-parallelogram",
  "phy-9-circuit-bulbs",
  "bio-12-dna",
  "bio-10-mitosis",
]);

// Experiments that have a full guided flow (Learn/Practice/Observe/Report tabs).
// Includes the original 3D-flagships PLUS extended experiments with Learn+Observe+Report only.
export const HAS_GUIDED = new Set([
  "phy-9-pendulum",
  "phy-11-parallelogram",
  "phy-9-circuit-bulbs",
  "bio-12-dna",
  "bio-10-mitosis",
  "phy-10-convex-lens",
  "bio-10-transpiration",
  "bio-10-blood-cells",
  "chem-9-ph",
  "chem-11-titration",
  "phy-11-resonance",
  // EXTENDED_V2 — 30 experiments added Feb 2026
  "phy-9-density", "phy-9-reflection", "phy-10-lever", "phy-10-prism",
  "phy-11-friction", "phy-11-spring", "phy-11-boyle", "phy-12-meterbridge",
  "phy-12-potentiometer", "phy-11-viscosity",
  "chem-9-flame-test", "chem-9-double-disp", "chem-10-acid-alkali",
  "chem-11-qualitative", "chem-11-chromato", "chem-11-alum",
  "chem-12-redox", "chem-12-rate", "chem-12-voltaic", "chem-12-func-group",
  "bio-9-microscope", "bio-9-food-test-9", "bio-9-germination",
  "bio-10-osmosis", "bio-10-photosynthesis", "bio-11-beetroot",
  "bio-11-plasmolysis", "bio-12-soil", "bio-12-enzyme", "bio-12-stomata",
]);

const PHASE_INDEX = { Interphase: 0, Prophase: 1, Metaphase: 2, Anaphase: 3, Telophase: 4 };

const NARRATION = {
  "phy-9-pendulum": (p) =>
    `Welcome to the Simple Pendulum experiment. You are looking at a three dimensional pendulum, with a bob of length ${((+p.length || 100) / 100).toFixed(2)} meters, released at an angle of ${p.angle || 10} degrees. The pendulum swings under gravity and its period is given by T equals two pi, times the square root of L over g. Watch how the swing slows when you increase length. Change any slider on the right to explore.`,
  "phy-11-parallelogram": (p) =>
    `This is the parallelogram law of forces in three dimensions. Force P equals ${p.P || 6} newtons in the horizontal direction. Force Q equals ${p.Q || 8} newtons, acting at an angle of ${p.angle || 60} degrees. The orange arrow is their resultant. The dashed lines complete the parallelogram. Increase the angle to see the resultant shrink.`,
  "phy-9-circuit-bulbs": (p) =>
    `This is a live three dimensional circuit. A battery of ${p.voltage || 6} volts drives current through two bulbs connected in ${(p.mode || "Series").toLowerCase()}. Notice how, in series, the same current flows through both bulbs, so they share the voltage. In parallel, each bulb gets full voltage, so both glow at maximum brightness. Change the mode to see the difference in glow.`,
  "bio-12-dna": () =>
    `You are looking inside a strand of DNA, magnified billions of times. The blue and pink spheres form the sugar phosphate backbones. Between them, four colored rungs represent the base pairs. Cyan is Adenine, amber is Thymine, purple is Guanine, and green is Cytosine. Adenine always pairs with Thymine, and Guanine with Cytosine. This is the double helix that stores every instruction to build life.`,
  "bio-10-mitosis": (p) =>
    `You are inside a cell in the ${(p.phase || "Metaphase")} phase of mitosis. During interphase, the DNA replicates. In prophase, chromosomes condense and the nuclear envelope breaks down. During metaphase, chromosomes line up along the center of the cell. In anaphase, sister chromatids are pulled apart to opposite poles. Finally in telophase, two new nuclei form. Change the phase on the right to watch the cell divide.`,
};

const Simulation3DCanvas = ({ experiment, params, hideNarrator = false }) => {
  const id = experiment.id;

  let scene = null;
  if (id === "phy-9-pendulum") {
    // length is in cm → convert to meters
    const L = Math.max(0.3, (+params.length || 100) / 100);
    scene = <Pendulum3D params={{ L, theta0: +params.angle || 10 }} />;
  } else if (id === "phy-11-parallelogram") {
    scene = <Parallelogram3D params={{ P: +params.P || 6, Q: +params.Q || 8, theta: +params.angle || 60 }} />;
  } else if (id === "phy-9-circuit-bulbs") {
    const mode = (params.mode || "Series").toLowerCase();
    const config = mode === "parallel" ? "parallel" : "series"; // "single" also handled visually as series with one bulb dimmed — MVP keeps 2 bulbs
    scene = <Circuit3D params={{ V: +params.voltage || 6, config }} />;
  } else if (id === "bio-12-dna") {
    scene = <DNAHelix3D />;
  } else if (id === "bio-10-mitosis") {
    const phaseIdx = PHASE_INDEX[params.phase] ?? 2;
    scene = <Mitosis3D params={{ phase: phaseIdx, progress: 0.5 }} />;
  }

  if (!scene) return null;

  const scriptFn = NARRATION[id];
  const script = scriptFn ? scriptFn(params) : null;

  return (
    <div className="relative w-full h-full">
      {scene}
      {script && !hideNarrator && <Narrator script={script} sceneKey={id} />}
    </div>
  );
};

export default Simulation3DCanvas;
